import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from '@phosphor-icons/react';
import api from '../lib/api';

const DEFAULT_CONTENT = {
  impressum: {
    title: 'Legal Notice',
    content: `## YuBlink GmbH

**Address:**
YuBlink GmbH
Example Street 42
10115 Berlin, Germany

**Contact:**
Email: info@yublink.com
Phone: +49 30 1234567

**Managing Director:** Max Mustermann
**Commercial Register:** District Court Berlin-Charlottenburg, HRB 123456
**VAT ID:** DE123456789

**Responsible for content (pursuant to Sec. 18 (2) of the German Media State Treaty – MStV):**
Max Mustermann, address as above.

**Dispute Resolution:**
The European Commission provides a platform for online dispute resolution (ODR):
https://ec.europa.eu/consumers/odr

We are not willing or obliged to participate in dispute resolution proceedings before a consumer arbitration board.`
  },
  datenschutz: {
    title: 'Privacy Policy (GDPR)',
    content: `## Privacy Policy

### 1. Data Controller
YuBlink GmbH, Example Street 42, 10115 Berlin, Germany
Email: privacy@yublink.com

### 2. Data We Collect
- **Account Data:** Name, email, date of birth, gender
- **Profile Data:** Photos, bio, heritage country, current city
- **Usage Data:** Chat messages, likes, blocks, reports
- **Payment Data:** Transaction IDs (processed via Stripe, we do not store card details)

### 3. Legal Basis
- **Contract (Art. 6(1)(b) GDPR):** For providing our service
- **Consent (Art. 6(1)(a) GDPR):** For optional marketing communications
- **Legitimate Interest (Art. 6(1)(f) GDPR):** For security and fraud prevention

### 4. Your Rights
You have the right to:
- Access your personal data
- Rectify inaccurate data
- Erase your data ("right to be forgotten")
- Restrict processing
- Data portability
- Object to processing

To exercise these rights, contact privacy@yublink.com

### 5. Data Retention
We retain your data for as long as your account is active. Upon account deletion, all personal data is permanently removed within 30 days.

### 6. Cookies
We use essential cookies for authentication. No tracking cookies are used without your consent.

### 7. Third-Party Services
- **Stripe:** Payment processing
- **Resend:** Email delivery
- **Cloud Storage:** Photo hosting

All third-party services are GDPR-compliant with appropriate data processing agreements in place.`
  },
  agb: {
    title: 'Terms of Service',
    content: `## General Terms and Conditions

### 1. Scope
These terms apply to all users of the YuBlink platform operated by YuBlink GmbH.

### 2. Registration
- Users must be at least 18 years old
- Users must provide accurate information
- Each person may only maintain one account

### 3. Services
- **Free:** Browse profiles, send up to 10 new chat messages per day
- **Premium (€10/month):** Unlimited chats, verified badge, priority in search

### 4. User Conduct
Users must not:
- Upload illegal, offensive, or inappropriate content
- Harass, stalk, or intimidate other users
- Use the platform for commercial purposes
- Create fake profiles or impersonate others

### 5. Content
Users retain ownership of their uploaded content but grant YuBlink a non-exclusive license to display it within the platform.

### 6. Termination
- Users may delete their account at any time via Settings
- YuBlink reserves the right to suspend or terminate accounts that violate these terms

### 7. Liability
YuBlink is not liable for:
- User-generated content
- Interactions between users outside the platform
- Service interruptions due to maintenance or force majeure

### 8. Cancellation of Premium
Premium subscriptions can be cancelled at any time. Access continues until the end of the billing period.

### 9. Applicable Law
These terms are governed by the laws of the Federal Republic of Germany. Place of jurisdiction is Berlin.`
  }
};

function renderMarkdown(text) {
  return text.split('\n').map((line, i) => {
    if (line.startsWith('### ')) return <h3 key={i} className="text-white font-['Outfit'] text-base mt-6 mb-3">{line.replace('### ', '')}</h3>;
    if (line.startsWith('## ')) return <h2 key={i} className="text-white font-['Outfit'] text-xl mt-8 mb-4">{line.replace('## ', '')}</h2>;
    if (line.startsWith('- ')) return <li key={i} className="ml-5 list-disc" dangerouslySetInnerHTML={{ __html: line.replace('- ', '').replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>') }} />;
    if (line.trim()) return <p key={i} dangerouslySetInnerHTML={{ __html: line.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>') }} />;
    return <br key={i} />;
  });
}

export default function LegalPage() {
  const { type } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLegal = async () => {
      try {
        const { data } = await api.get(`/legal/${type}`);
        if (data.page) {
          setPage(data.page);
        } else {
          setPage(DEFAULT_CONTENT[type] || null);
        }
      } catch {
        setPage(DEFAULT_CONTENT[type] || null);
      }
      setLoading(false);
    };
    fetchLegal();
  }, [type]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!page) {
    navigate('/');
    return null;
  }

  return (
    <div className="min-h-screen pt-20 pb-8 px-4" data-testid={`legal-${type}-page`}>
      <div className="max-w-2xl mx-auto">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#9EABCE] hover:text-white mb-6 transition-colors">
          <ArrowLeft size={20} /> Back
        </button>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-8">
          <h1 className="text-3xl font-semibold text-white font-['Outfit'] mb-6">{page.title}</h1>
          <div className="text-[#9EABCE] text-sm leading-relaxed space-y-1">
            {renderMarkdown(page.content)}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
